import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ArrowLeftFilled from '@uber/base-icons/arrow-left-filled';
import { styled } from 'baseui';
import { Button, KIND, SHAPE } from 'baseui/button';
import { HeadingXSmall } from 'baseui/typography';
import useShortcut from '../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../image/labelling/providers/utils/shortcut';
import type { FrameSpanT } from '../../image/labelling/reducers/actions';
import { ACTION_BY, NOTIFICATION_TYPE } from '../constant/constant';
import Divider from '../divider/divider';
import useStorage from '../hooks/useStorage';
import type { QuestionnairePayloadT } from '../side-panel/analytics/types';
import useAnalyticsForSidePanel from '../side-panel/analytics/useAnalyticsForSidePanel';
import type { AnnotationLabelT, AnswerT, GenericAnnotationT, GetAnnotationMetaDetailsT } from '../types/types';
import { isQuestionnaireUpdated } from '../utils/utils';
import useDynamicQuestionnaire from './dynamic-questionnaire/useDynamicQuestionnaire';
import QuestionnaireSection from './questionnaire-section/questionnaire-section';
import AnnotationEditor from './annotation-editor';
import { adaptToFrameSpan, getAnswersStorageKey, parseLocallyStoredAnswers, partitionStaticAndDynamicQuestionnaire } from './utils';
const buttonOverride = {
  BaseButton: {
    style: {
      width: '100%'
    }
  }
};
const SidePopoverContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  width: '100%'
});
const ControlContainer = styled('div', {
  maxHeight: '95%',
  overflowY: 'auto'
});
const ActionContainer = styled('div', {
  display: 'flex',
  gap: '10px'
});
const deleteButtonOverride = {
  BaseButton: {
    style: ({
      $theme
    }) => ({
      ...buttonOverride.BaseButton.style,
      color: $theme.colors.negative
    })
  }
};
const HeadingContainer = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  gap: $theme.sizing.scale100
}));
type PropsT = {
  annotationLabels: AnnotationLabelT[];
  selectedAnnotation: GenericAnnotationT;
  editableAnnotationId: string;
  onCancel: () => void;
  showNotification: (notificationType: (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE], message: string) => void;
  onUpdateAnnotation: (b: string, a: {
    newLabel?: AnnotationLabelT;
    newAnswers?: AnswerT;
  }) => void;
  onDelete?: (annotationIdx: string) => void;
  checkLabelMaxReached: (annotationLabel: AnnotationLabelT) => boolean;
  getAnnotationMetaDetails: GetAnnotationMetaDetailsT;
  isQuestionnaireComposable?: boolean;
  allowedAnnotationLabels?: AnnotationLabelT[];
  getSelectedAnnotationFrameRange?: (annotationId: string) => {
    startFrameIndex: number;
    endFrameIndex: number;
  };
  updateDynamicAnnotation: (annotationId: string, dynamicAnnotation: GenericAnnotationT, range: FrameSpanT) => void;
  getDynamicAnswersRange?: (annotationId: string) => {
    frameStart: number;
    frameEnd: number;
    annotation: GenericAnnotationT;
    isValid: boolean;
    id: string;
    savedToStore: boolean;
  }[];
  resetDynamicFields: (annotationId: string, frameSpan: FrameSpanT) => void;
  isDynamicAttributeSupported: boolean;
  getAnnotationDataDetail?: () => React.ReactNode;
  onValidateAnswers?: (annotationId: string, answers: AnswerT) => Promise<any[]>;
};
const HEADING_OVERRIDE = {
  Block: {
    style: ({
      $theme
    }) => ({
      marginTop: $theme.sizing.scale300,
      marginBottom: $theme.sizing.scale300,
      flexGrow: '1'
    })
  }
};
const SidePopoverPanel = ({
  annotationLabels,
  editableAnnotationId,
  onCancel,
  showNotification,
  onUpdateAnnotation,
  onDelete,
  checkLabelMaxReached,
  selectedAnnotation,
  isQuestionnaireComposable,
  getAnnotationMetaDetails,
  allowedAnnotationLabels,
  getSelectedAnnotationFrameRange,
  updateDynamicAnnotation,
  getDynamicAnswersRange,
  resetDynamicFields,
  isDynamicAttributeSupported,
  getAnnotationDataDetail,
  onValidateAnswers
}: PropsT) => {
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const {
    get,
    set
  } = useStorage();
  const locallyStoredAnswers = useMemo(() => parseLocallyStoredAnswers(get(getAnswersStorageKey(selectedAnnotation))), [get, selectedAnnotation]);
  const [annotation, setAnnotation] = useState<GenericAnnotationT>(() => {
    const hasLocalAnswers = locallyStoredAnswers && Object.keys(locallyStoredAnswers).length > 0;
    if (hasLocalAnswers) {
      return {
        ...selectedAnnotation,
        metadata: {
          answers: locallyStoredAnswers
        }
      };
    }
    return selectedAnnotation;
  });
  const [isQuestionnaireValid, setQuestionnaireValid] = useState(true);
  const {
    sendEventForQuestionnaireSkipped,
    sendEventForQuestionnaireAnswered,
    sendEventForQuestionnaireUpdated,
    sendEventForUpdateAnnotationLabel
  } = useAnalyticsForSidePanel();
  const {
    registerHotKeys
  } = useShortcut();
  React.useEffect(() => {
    setAnnotation(locallyStoredAnswers ? {
      ...selectedAnnotation,
      metadata: {
        answers: locallyStoredAnswers
      }
    } : selectedAnnotation);
  }, [locallyStoredAnswers, selectedAnnotation]); // without this useEffect, the annotation is not updated when the selectedAnnotation changes

  const DynamicQuestion = useDynamicQuestionnaire({
    getDynamicAnswersRange,
    selectedAnnotationId: selectedAnnotation.id,
    annotation,
    resetDynamicFields,
    getSelectedAnnotationFrameRange,
    showNotification,
    updateDynamicAnnotation,
    editableAnnotationId,
    validateDynamicAnnotation: () => true
  });
  const questionnaireEventPayload: QuestionnairePayloadT = useMemo(() => ({
    objectId: editableAnnotationId,
    objectType: annotation.annotationType,
    label: annotation.annotationLabel.title
  }), [annotation.annotationLabel.title, annotation.annotationType, editableAnnotationId]);
  const handleUpdateAnnotationDetails = useCallback((updatedAnnotationDetails: {
    newLabel: AnnotationLabelT;
  }) => {
    if (isDynamicAttributeSupported && getSelectedAnnotationFrameRange) {
      resetDynamicFields(selectedAnnotation.id, adaptToFrameSpan(getSelectedAnnotationFrameRange(selectedAnnotation.id)));
      DynamicQuestion.resetDynamicAnnotationConfig();
    }
    setAnnotation({
      ...annotation,
      annotationLabel: updatedAnnotationDetails.newLabel,
      metadata: {
        answers: {}
      }
    });
  }, [annotation, getSelectedAnnotationFrameRange, selectedAnnotation, resetDynamicFields, DynamicQuestion, isDynamicAttributeSupported]);
  const handleOnQuestionnaireChange = useCallback((isAnswerValid: boolean, updatedAnswers?: AnswerT) => {
    setQuestionnaireValid(isAnswerValid);
    if (updatedAnswers) {
      set(getAnswersStorageKey(selectedAnnotation), JSON.stringify(updatedAnswers));
      setAnnotation({
        ...annotation,
        metadata: {
          ...annotation.metadata,
          answers: updatedAnswers
        }
      });
    }
  }, [annotation, selectedAnnotation, set]);
  const handleSave = useCallback(async () => {
    if (annotation.annotationLabel.questionnaire && !isQuestionnaireValid) {
      showNotification(NOTIFICATION_TYPE.ERROR, 'Complete all required fields or correct invalid inputs.');
      return;
    }
    const hasAnswersForValidation = annotation.metadata?.answers && Object.keys(annotation.metadata.answers).length > 0;
    if (hasAnswersForValidation && annotation.metadata?.answers && onValidateAnswers) {
      setIsValidationLoading(true);
      try {
        const validationResults = await onValidateAnswers(editableAnnotationId, annotation.metadata.answers);
        if (validationResults.length > 0) {
          return;
        }
      } catch {
        // TODO: handle error with logger
      } finally {
        setIsValidationLoading(false);
      }
    }

    // checking updated answers object length
    const hasAnswers = annotation.metadata?.answers && Object.keys(annotation.metadata?.answers).length > 0;
    if (hasAnswers) {
      const questionnaireUpdated = isQuestionnaireUpdated(selectedAnnotation, annotation);
      questionnaireUpdated ? sendEventForQuestionnaireUpdated(questionnaireEventPayload) : !selectedAnnotation.metadata?.answers && sendEventForQuestionnaireAnswered(questionnaireEventPayload);
    }
    if (selectedAnnotation.annotationLabel.title !== annotation.annotationLabel.title) {
      sendEventForUpdateAnnotationLabel({
        objectType: annotation.annotationType,
        prevLabel: selectedAnnotation.annotationLabel.title,
        newLabel: annotation.annotationLabel.title,
        objectId: editableAnnotationId,
        actionBy: ACTION_BY.EDIT
      });
    }
    set(getAnswersStorageKey(selectedAnnotation), JSON.stringify(undefined));
    onUpdateAnnotation(editableAnnotationId, {
      newLabel: annotation.annotationLabel,
      newAnswers: annotation.metadata?.answers
    });
  }, [annotation, editableAnnotationId, isQuestionnaireValid, onUpdateAnnotation, onValidateAnswers, questionnaireEventPayload, selectedAnnotation, sendEventForQuestionnaireAnswered, sendEventForQuestionnaireUpdated, sendEventForUpdateAnnotationLabel, set, showNotification]);
  const handleOnClose = useCallback(() => {
    if (!selectedAnnotation.metadata?.answers) {
      sendEventForQuestionnaireSkipped(questionnaireEventPayload);
    }
    onCancel();
  }, [onCancel, questionnaireEventPayload, selectedAnnotation, sendEventForQuestionnaireSkipped]);
  useEffect(() => {
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['Escape'],
      onAction: handleOnClose
    }, {
      keys: ['Shift', 'Alt', 's'],
      onAction: handleSave
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, handleOnClose, handleSave]);
  return <SidePopoverContainer>
      <ControlContainer>
        <HeadingContainer>
          <Button
        // @ts-expect-error todo(ts-migration) TS2322 Type '{ children: Element; $style: ({ $theme }: { $theme: any; }) => { marginLeft: string; }; shape: "circle"; kind: "tertiary"; onClick: () => void; }' is not assignable to type 'IntrinsicAttributes ...
        $style={({
          $theme
        }) => ({
          marginLeft: `-${$theme.sizing.scale300}`
        })} shape={SHAPE.circle} kind={KIND.tertiary} onClick={handleOnClose}>
            <ArrowLeftFilled />
          </Button>
          <HeadingXSmall overrides={HEADING_OVERRIDE}>Annotation Details</HeadingXSmall>
        </HeadingContainer>
        <AnnotationEditor annotation={annotation} annotationLabels={annotationLabels} editableAnnotationId={editableAnnotationId} onAnnotationUpdate={annotationLabel => handleUpdateAnnotationDetails({
        newLabel: annotationLabel
      })} checkLabelMaxReached={checkLabelMaxReached} getAnnotationMetaDetails={getAnnotationMetaDetails} allowedAnnotationLabels={allowedAnnotationLabels} getAnnotationDataDetail={getAnnotationDataDetail} />
        <Divider />
        {annotation.annotationLabel.questionnaire?.map(questionnaire => <QuestionnaireSection key={`${annotation.annotationLabel.title}-${annotation.id}`} questionnaireId={questionnaire.questionnaireId} annotation={annotation} onQuestionnaireChange={handleOnQuestionnaireChange} isQuestionnaireComposable={isQuestionnaireComposable} questionFilterFn={response => {
        const {
          staticQuestionnaire
        } = partitionStaticAndDynamicQuestionnaire(response);
        return staticQuestionnaire;
      }} />)}
        <Divider />
      </ControlContainer>
      <ActionContainer>
        {!onDelete && <Button overrides={buttonOverride} kind={KIND.secondary} onClick={handleOnClose}>
            Cancel
          </Button>}
        {onDelete && editableAnnotationId && <Button overrides={deleteButtonOverride} kind={KIND.secondary} onClick={() => onDelete(editableAnnotationId)}>
            Delete
          </Button>}
        <Button onClick={handleSave} overrides={buttonOverride} isLoading={isValidationLoading} disabled={isValidationLoading}>
          Save
        </Button>
      </ActionContainer>
    </SidePopoverContainer>;
};
export default SidePopoverPanel;